// Angular
import { NgModule } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { A11yModule } from '@angular/cdk/a11y';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { CdkTableModule } from '@angular/cdk/table';
import { RouterModule } from '@angular/router';

// Components
import { AppSpinnerComponent, SplashScreenComponent, ColorPickerComponent, DynamicTableComponent, DynamicEditFormComponent, CustomSnackbarComponent, OnlineIndicatorComponent } from './components';
import { JobChartOldComponent } from '../modules/job/components/job-chart-old/job-chart-old.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import { UserEditSimpleDialogComponent } from '../dialogs/user-edit-dialog/user-edit-dialog.component';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { BrandComponent } from './components/brand/brand.component';
import { HeaderComponent } from './components/header/header.component';
import { HeaderMenuComponent } from './components/header-menu/header-menu.component';
import { DynamicSideNavComponent } from './components/dynamic-side-nav/dynamic-side-nav.component';
import { SubheaderComponent } from './components/subheader/subheader.component';
import { BaseComponent } from './components/base/base.component';
import { ScrollToTopComponent } from './components/scroll-to-top/scroll-to-top.component';
import { ConfirmDialogComponent } from './dialogs/confirm-dialog/confirm-dialog.component';
import { FileUploadDialogComponent } from './dialogs/file-upload-dialog/file-upload-dialog.component';
import { ImageDisplayDialogComponent } from './dialogs/image-display-dialog/image-display-dialog.component';
import { NamingDialogComponent } from './dialogs/naming-dialog/naming-dialog.component';
import { ValueDialogComponent } from './dialogs/value-dialog/value-dialog.component';
import { CommentLogComponent } from '../modules/job/components/comment-log/comment-log.component';
import { SyncSpinnerComponent } from './components/sync-spinner/sync-spinner.component';
import { CommentLogTooltipComponent } from '../modules/job/components/comment-log-tooltip/comment-log-tooltip.component';
import { JobOverviewComponent } from '../modules/job/components/job-overview/job-overview.component';
import { JobChannelDisplayComponent } from '../modules/job/components/job-channel-display/job-channel-display.component';
import { JobChartComponent } from '../modules/job/components/job-chart/job-chart.component';
import { JobOverviewMobileComponent } from '../modules/job/components/job-overview/components/job-overview-mobile/job-overview-mobile.component';
import { FilterManuComponent } from './components/filter-menu/filter-menu.component';
import { ThemeSettingsDialogComponent } from '../dialogs/theme-settings-dialog/theme-settings-dialog.component';
import { UserChannelSettingDialogComponent } from '../dialogs/user-channel-settings-dialog/user-channel-settings-dialog.component';

// Modules
import { MaterialModule } from './material.module';
import { TranslateModule } from '@ngx-translate/core';

// Pipes
import { DefaultPipe, FirstLetterPipe, GetObjectPipe, JoinPipe, SafePipe, TimeElapsedPipe } from './pipes';
import { MilitaryTimePipe, MilitaryTimeDatePipe } from './pipes';

// Directives
import { IsGrantedDirective } from './directives';
import { ClickOutsideDirective } from './directives';

// Libraries
import { ColorCircleModule } from 'ngx-color/circle';
import { ColorSketchModule } from 'ngx-color/sketch';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { DeviceDetectorService } from 'ngx-device-detector';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { ChartModule } from 'angular-highcharts';
import { DateTimeRangePickerComponent } from './components/date-time-range-picker/date-time-range-picker.component';

@NgModule({
	imports: [
		// Angular Modules
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		MatSidenavModule,
		
		// Angular CDK Modules
		A11yModule,
		DragDropModule,
		OverlayModule,
		PortalModule,
		CdkTableModule,
		
		// App Modules
		MaterialModule,
		TranslateModule,
		RouterModule,
		NgbModule,
		NgxDaterangepickerMd.forRoot(),
		ColorCircleModule,
		ColorSketchModule,
		NgxMaskDirective,
		NgxMaskPipe,
		ChartModule,
		DateTimeRangePickerComponent,
	],
	declarations: [
		// App Components
		BaseComponent,
		AppSpinnerComponent,
		SyncSpinnerComponent,
		SplashScreenComponent,
		SideNavComponent,
		DynamicSideNavComponent,
		BrandComponent,
		HeaderComponent,
		HeaderMenuComponent,
		SubheaderComponent,

		// Components
		DynamicTableComponent,
		DynamicEditFormComponent,
		JobChartOldComponent,
		JobOverviewComponent,
		JobOverviewMobileComponent,
		JobChannelDisplayComponent,
		JobChartComponent,
		CommentLogComponent,
		CommentLogTooltipComponent,
		CustomSnackbarComponent,
		UserEditSimpleDialogComponent,
		UserChannelSettingDialogComponent,
		OnlineIndicatorComponent,
		ScrollToTopComponent,
		ConfirmDialogComponent,
		FileUploadDialogComponent,
		ImageDisplayDialogComponent,
		NamingDialogComponent,
		ValueDialogComponent,
		FilterManuComponent,
		ThemeSettingsDialogComponent,

		// Directives
		IsGrantedDirective,
		ClickOutsideDirective,

		// Pipes
		DefaultPipe,
		FirstLetterPipe,
		GetObjectPipe,
		JoinPipe,
		SafePipe,
		TimeElapsedPipe,
		ColorPickerComponent,
		MilitaryTimePipe,
		MilitaryTimeDatePipe,
	],
	exports: [
		// Angular Modules
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		MatSidenavModule,

		// Angular CDK Modules
		A11yModule,
		DragDropModule,
		OverlayModule,
		TranslateModule,
		CdkTableModule,

		// App Modules
		MaterialModule,
		PortalModule,
		// App Components
		NgxMaskDirective,
		NgxMaskPipe,

		BaseComponent,
		AppSpinnerComponent,
		SyncSpinnerComponent,
		SplashScreenComponent,
		SideNavComponent,
		DynamicSideNavComponent,
		BrandComponent,
		HeaderComponent,
		HeaderMenuComponent,
		SubheaderComponent,
		ColorPickerComponent,
		DynamicTableComponent,
		DynamicEditFormComponent,
		JobChartOldComponent,
		JobOverviewComponent,
		JobOverviewMobileComponent,
		JobChannelDisplayComponent,
		JobChartComponent,
		CommentLogComponent,
		CommentLogTooltipComponent,
		CustomSnackbarComponent,
		OnlineIndicatorComponent,
		UserEditSimpleDialogComponent,
		UserChannelSettingDialogComponent,
		ScrollToTopComponent,
		ConfirmDialogComponent,
		FileUploadDialogComponent,
		ImageDisplayDialogComponent,
		NamingDialogComponent,
		ValueDialogComponent,
		FilterManuComponent,
		ThemeSettingsDialogComponent,

		// Pipes
		DefaultPipe,
		FirstLetterPipe,
		GetObjectPipe,
		JoinPipe,
		SafePipe,
		TimeElapsedPipe,
		MilitaryTimePipe,
		MilitaryTimeDatePipe,

		// Directives
		IsGrantedDirective,
		ClickOutsideDirective,
	],
	providers: [DatePipe, DecimalPipe, DeviceDetectorService, provideNgxMask()],
})
export class SharedModule {}
